import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { FLOWS, stepState, type FlowId } from '@/lib/flows';
import { useT } from '@/i18n';
import { isResizing } from '@/lib/resizing';
import { useLatest } from '@/lib/useLatest';
import styles from './AppCard.module.css';
import shared from '@/pages/pages.module.css';

import { CheckIcon, ChevronDownIcon } from './Icons';
import type { AppCardProps } from './AppCard.types';

/** A live detail under the current step, such as how much of a file is uploaded. */
export interface StepNote {
  readonly text: string;
  /** 0–1 for a progress bar under the text; null for none. */
  readonly fraction: number | null;
  readonly label?: string;
}

/** The step guide: grey for steps ahead, blue for the current one, green with a check for the done ones. */
export function FlowGuide({
  flow,
  progress,
  optional = false,
  note = null,
  footer = null,
}: {
  readonly flow: FlowId;
  readonly progress: number | null;
  readonly optional?: boolean;
  readonly note?: StepNote | null;
  /** Under the steps, such as the way into “How Bridge works”. */
  readonly footer?: ReactNode;
}): React.JSX.Element {
  const t = useT();
  const { title, steps } = FLOWS[flow];
  // under the tool on tablets and phones: folded to one row, the current step and a dot for each step, opened by the chevron
  const [open, setOpen] = useState(false);
  const current = steps.find((_, index) => stepState(index, progress) === 'current') ?? steps[0];
  return (
    <aside
      className={[shared.guide, optional ? shared.guideOptional : '', footer === null ? '' : shared.guideWithFooter].join(' ')}
      aria-label={title}
      data-testid="flow-guide"
      data-flow={flow}
      data-progress={progress === null ? 'none' : String(progress)}
      data-open={open}
    >
      <p className={shared.guideTitle}>{title}</p>
      {/* folds the guide on narrow screens: the steps of a flow, and on a phone the first screen's overview too */}
      <button
        type="button"
        className={shared.guideToggle}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
        data-testid="flow-guide-toggle"
      >
        {/* a dot for each step, then the current step's name: green done, blue now, grey ahead — no numbers to crowd the words */}
        {optional ? null : (
          <span className={shared.guideMini} aria-hidden="true">
            {steps.map((step, index) => (
              <i key={step.title} data-state={stepState(index, progress)} />
            ))}
          </span>
        )}
        <span className={shared.guideNow}>{open || optional ? title : (note?.text ?? current?.title ?? title)}</span>
        <ChevronDownIcon size={18} className={shared.guideChevron} />
      </button>

      <ol className={styles.guideSteps}>
        {steps.map((step, index) => {
          const state = stepState(index, progress);
          return (
            <li key={step.title} className={shared.guideStep} data-state={state} aria-current={state === 'current' ? 'step' : undefined} data-testid="flow-step">
              <span className={shared.guideMark} aria-hidden="true">
                {state === 'done' ? <CheckIcon size={13} strokeWidth={2.6} /> : index + 1}
              </span>
              <span className={styles.guideBody}>
                <span className={shared.guideStepTitle}>
                  {step.title}
                  <span className="visually-hidden">{state === 'done' ? ` ${t('appCard.stepDone')}` : state === 'current' ? ` ${t('appCard.stepCurrent')}` : ''}</span>
                </span>
                <span className={styles.guideStepText}>{step.text}</span>
                {state === 'current' && note !== null ? (
                  <span className={styles.guideNote} data-testid="step-note">
                    {note.text}
                    {note.fraction === null ? null : (
                      <span className={styles.guideNoteBar} role="progressbar" aria-label={note.label ?? note.text} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(note.fraction * 100)}>
                        <span style={{ width: `${String(Math.round(note.fraction * 100))}%` }} />
                      </span>
                    )}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      {footer}
    </aside>
  );
}

/** The guide's place while the content is loading: its outline, without words. */
export function GuideSkeleton(): React.JSX.Element {
  return (
    <aside className={shared.guide} aria-hidden="true" data-testid="guide-skeleton">
      <span className={[shared.skeleton, shared.skeletonLabel].join(' ')} />
      <span className={styles.guideSteps}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={styles.skeletonStep}>
            <span className={[shared.skeleton, shared.skeletonDot].join(' ')} />
            <span className={shared.skeletonLines}>
              <span className={[shared.skeleton, shared.skeletonLine].join(' ')} style={{ width: `${String(62 - i * 6)}%` }} />
              <span className={[shared.skeleton, shared.skeletonLine, shared.skeletonLineSoft].join(' ')} style={{ width: `${String(84 - i * 4)}%` }} />
            </span>
          </span>
        ))}
      </span>
    </aside>
  );
}

/** The height of the last card on screen, so the next one can grow or shrink from it. */
let lastCardHeight: number | null = null;
/** How many cards this page load has shown: only the very first unfolds. */
let cardsShown = 0;
/** Changes smaller than this (a pixel of rounding, a border) are not worth a glide. */
const GLIDE_MIN = 4;
const GLIDE_MS = 320;
const GLIDE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Keeps the frame of the card steady: when its height jumps (another step,
 * another screen in the same window), the frame glides from the old height to
 * the new one instead, in one move. The content inside takes its new height
 * at once; the frame clips it only while it glides. If the content changes
 * again during a glide, the frame carries on from where it is to the newest
 * height when the glide ends, so it never stops halfway.
 */
function useGlidingFrame(fresh: boolean): React.RefObject<HTMLElement | null> {
  const frame = useRef<HTMLElement>(null);
  const isFresh = useLatest(fresh);
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el || typeof el.animate !== 'function') return undefined;
    let current = el.getBoundingClientRect().height;
    let glide: Animation | null = null;
    const run = (from: number, to: number): void => {
      if (reducedMotion() || Math.abs(from - to) < GLIDE_MIN) return;
      glide?.cancel();
      el.style.overflow = 'hidden';
      const animation = el.animate([{ height: `${String(from)}px` }, { height: `${String(to)}px` }], { duration: GLIDE_MS, easing: GLIDE_EASE });
      glide = animation;
      animation.onfinish = () => {
        if (glide !== animation) return;
        glide = null;
        el.style.overflow = '';
        // the content may have moved on while the frame was gliding: follow it from here
        const natural = el.getBoundingClientRect().height;
        if (Math.abs(natural - to) >= GLIDE_MIN) run(to, natural);
        current = natural;
        lastCardHeight = natural;
      };
      animation.oncancel = () => {
        if (glide !== animation) return;
        glide = null;
        el.style.overflow = '';
      };
    };
    if (isFresh.current && lastCardHeight !== null) run(lastCardHeight, current);
    lastCardHeight = current;
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(() => {
      // the window is being resized or zoomed: the frame follows at once, a glide would lag behind the pointer
      if (isResizing()) {
        glide?.cancel();
        current = el.getBoundingClientRect().height;
        lastCardHeight = current;
        return;
      }
      // while gliding the frame's height is the animation's; the end of the glide picks up any change
      if (glide) return;
      const next = el.getBoundingClientRect().height;
      run(current, next);
      current = next;
      lastCardHeight = next;
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      glide?.cancel();
    };
    // the frame is measured once per card; later changes come through the observer
  }, [isFresh]);
  return frame;
}

/**
 * The app window: one card as wide as the page column, the tool on the left
 * and the step guide on the right (below it on tablets and phones). The card itself never
 * jumps between steps or screens: only its content moves, and its frame glides
 * to the new height. Only the first card of a visit unfolds; a card that takes
 * over from another one keeps the frame and fades its content in.
 */
export function AppCard({ children, guide, stepKey, direction = 'forward', className, testId, busy, mode, role, status, enter = true }: AppCardProps): React.JSX.Element {
  const [first] = useState(() => {
    cardsShown += 1;
    return cardsShown === 1;
  });
  const fresh = !first && enter;
  const frame = useGlidingFrame(!first);
  // Without an entrance the first content stays put; later steps slide as usual.
  const [quietKey, setQuietKey] = useState<string | undefined | null>(enter && first ? null : stepKey);
  if (quietKey !== null && stepKey !== quietKey) setQuietKey(null);
  const slide = stepKey === undefined || stepKey === quietKey ? undefined : direction === 'back' ? shared.stepBack : shared.stepForward;
  return (
    <section
      ref={frame}
      className={[shared.card, shared.appCard, guide ? shared.appSplit : shared.appSingle, enter && first ? shared.unfold : '', fresh ? shared.fresh : '', className ?? ''].join(' ')}
      aria-busy={busy}
      data-testid={testId}
      data-mode={mode}
      data-status={status}
      role={role}
    >
      <div className={shared.appMain}>
        {/* the content takes its new height at once; only the frame glides, in one move (see useGlidingFrame) */}
        <div key={stepKey} className={slide}>
          {children}
        </div>
      </div>
      {guide}
    </section>
  );
}
